import { forwardRef, memo, useCallback } from 'react';
import type { ComponentPropsWithoutRef } from 'react';

import { cx } from '@/utils/cx';

import { useActionContext } from '../CategoryContext';

import styles from './CategoryItem.module.scss';

export interface CategoryItemProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children'> {
  /** 아이템 텍스트 */
  label: string;
  /** onAction 인자. Category의 activeAction과 비교하여 활성 상태 자동 판단. */
  action: string;
  /** onAction 콜백에 전달할 메타데이터 */
  meta?: Record<string, unknown>;
}

export const CategoryItem = memo(
  forwardRef<HTMLButtonElement, CategoryItemProps>(
    ({ label, action, meta = {}, onClick, className, 'aria-label': ariaLabel, ...rest }, ref) => {
      const { onAction, activeAction } = useActionContext();
      const active = activeAction === action;

      const handleClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
          onClick?.(e);
          if (!e.defaultPrevented) {
            onAction?.(action, meta, e);
          }
        },
        [onClick, action, onAction, meta],
      );

      return (
        <button
          aria-current={active ? 'page' : undefined}
          aria-label={ariaLabel ?? label}
          className={cx(styles.item, active && styles.itemActive, className)}
          data-action={action}
          onClick={handleClick}
          ref={ref}
          type="button"
          {...rest}
        >
          {label}
        </button>
      );
    },
  ),
);

CategoryItem.displayName = 'Category.Item';
